import React, { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from '@/app/core/auth/application/hooks/useAuth';
import { analytics } from '@/platform/analytics';
import { Spinner } from '@/app/shared/ui';

interface PrivateRouteProps {
  children: ReactNode;
  /** Where an unauthenticated visitor is sent. */
  to: string;
}

/**
 * `exp` claim in seconds; `undefined` when the token carries no `exp` (allowed
 * through, as before); `null` when the token cannot be decoded at all.
 */
const readTokenExpiry = (token: string): number | null | undefined => {
  try {
    const payloadBase64 = token.split('.')[1];
    const decodedPayload = JSON.parse(atob(payloadBase64)) as { exp?: unknown };
    return typeof decodedPayload.exp === 'number' ? decodedPayload.exp : undefined;
  } catch {
    return null;
  }
};

export const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, to }) => {
  const { isAuthenticated, isLoading, doLogout, token } = useAuth();

  // The expiry check ends the session, so it is a side effect and belongs in an
  // effect, not in the render path: reading the clock during render is impure,
  // and under StrictMode the old placement fired the analytics event and the
  // logout twice. doLogout() clears the token, which re-renders this component
  // with no token and takes the redirect branch below.
  React.useEffect(() => {
    if (!token) return;
    const exp = readTokenExpiry(token);
    const isExpired = exp === null || (exp !== undefined && exp * 1000 < Date.now());
    if (!isExpired) return;
    analytics.capture('auth:session_ended', { reason: 'expired' });
    doLogout();
  }, [token, doLogout]);

  if (isLoading) {
    return (
      <div className="w-full min-h-screen flex justify-center items-center bg-[var(--tk-surface-bg)]">
        <Spinner size="lg" tone="brand" label="Loading session" />
      </div>
    );
  }
  if (!token) {
    return <Navigate to={to} replace />;
  }
  return isAuthenticated ? <>{children}</> : <Navigate to={to} replace />;
};
